/**
 * The guest surface, end to end. S7.1 and S7.2.
 *
 * ## Routing, or the lack of it
 *
 * Two locations exist: `/g/{token}` and everything else. That is not enough to justify a router, and
 * a router would be a dependency whose whole job is a `startsWith` here. nginx already serves
 * `index.html` for unknown paths, so a deep link works without one.
 *
 * ## The token leaves the address bar
 *
 * A grant token is a single-use credential travelling in a URL path, which section 08 accepts as
 * unavoidable while forbidding it from reaching a log line, an error message or a redirect target.
 * Once it has been exchanged for a session there is no reason for it to stay visible, so
 * `history.replaceState` scrubs it: it stops the token being shared when somebody sends a colleague
 * "the page I'm on", and it stops a reload re-opening a grant that is now spent.
 *
 * ## One upload at a time
 *
 * Deliberate. This is a phone on cellular, and two concurrent 350MB transfers compete for the same
 * pipe and produce two progress bars that both crawl. Section 09's resume behaviour is per upload, so
 * serialising also means one thing to explain when a connection drops.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  challengeIssuedSchema,
  guestChecklistSchema,
  guestDocumentListSchema,
  guestExchangeListSchema,
  guestExchangeSchema,
  genericLinkInfoSchema,
  openedGrantSchema,
  signedInSchema,
  verifiedSessionSchema,
} from '@iqz-ddds/contracts';
import { Notice, Page } from '@iqz-ddds/components';
import { request } from './api/client';
import { FailureNotice } from './components/FailureNotice';
import { CodeScreen } from './screens/CodeScreen';
import { ExchangeListScreen } from './screens/ExchangeListScreen';
import { ExchangeScreen, type UploadOutcome, type UploadState } from './screens/ExchangeScreen';
import { GenericUploadScreen } from './screens/GenericUploadScreen';
import { GrantScreen } from './screens/GrantScreen';
import { IdentifyScreen } from './screens/IdentifyScreen';
import { SignInScreen } from './screens/SignInScreen';
import { uploadFile, uploadGenericFile, type UploadHandle } from './upload/tus';
import type { ChallengeIssued, GuestChecklistItem, GuestDocument, GuestExchange, GuestExchangeList, OpenedGrant } from '@iqz-ddds/contracts';

/** Where the flow is. A union rather than a pile of booleans: the states are genuinely exclusive. */
type Phase =
  | { readonly kind: 'loading' }
  /**
   * No token and no session. The root of the surface, and since D-25 it is a way in rather
   * than a dead end: it used to say "open the link in your email", which was honest and
   * useless to anybody who had deleted it.
   */
  | { readonly kind: 'sign_in' }
  /** The code, after signing in with an address. Distinct from `code`, which has a grant. */
  | { readonly kind: 'sign_in_code'; readonly email: string; readonly genericLinkToken?: string }
  | { readonly kind: 'generic_identify'; readonly repName?: string | undefined }
  /** The OTP step a generic-link visitor must clear before `generic_upload` is reachable. */
  | { readonly kind: 'generic_code'; readonly email: string; readonly genericLinkToken: string; readonly repName?: string | undefined }
  | { readonly kind: 'generic_upload'; readonly genericLinkToken: string; readonly repName?: string | undefined }
  | { readonly kind: 'grant'; readonly grant: OpenedGrant }
  | { readonly kind: 'code'; readonly grant: OpenedGrant; readonly challenge: ChallengeIssued }
  /**
   * Signed in, looking at everything. D-25's new state.
   *
   * Skipped when there is exactly one exchange, which is the ordinary case and would otherwise be a
   * list of one that every visitor has to click through.
   */
  | { readonly kind: 'exchanges'; readonly list: GuestExchangeList }
  | { readonly kind: 'exchange'; readonly exchangeId: string }
  | { readonly kind: 'dead_link'; readonly detail: string };

interface ExchangeData {
  readonly exchange: GuestExchange;
  readonly requests: readonly GuestChecklistItem[];
  readonly documents: readonly GuestDocument[];
}

/** The token from `/g/{token}`, or null. Never logged and never put in a query parameter. */
export function tokenFromPath(pathname: string): string | null {
  const match = /^\/g\/([^/?#]+)/.exec(pathname);
  return match === null ? null : decodeURIComponent(match[1] as string);
}

export function genericTokenFromPath(pathname: string): string | null {
  const match = /^\/upload\/([^/?#]+)/.exec(pathname);
  return match === null ? null : decodeURIComponent(match[1] as string);
}

/**
 * What to say once the pipeline has finished with an upload.
 *
 * Only three of section 09's states reach a person. `committed` needs nothing said - the document is
 * in the list they are looking at, which is the whole confirmation - so it produces no notice at all.
 * The other two do, because in both of them the file the prospect just sent is not going to appear
 * and silence would read as it having been lost.
 *
 * The scanner's own words are appended where there are any. `uploadStatusSchema` is explicit that
 * these are safe to show: a signature name is a fact about the file rather than a secret, and
 * refusing a document without saying why produces a support call.
 */
function outcomeOf(handle: UploadHandle, filename: string): UploadOutcome | null {
  const because = handle.detail === null || handle.detail === '' ? '' : ` (${handle.detail})`;

  if (handle.state === 'rejected') {
    return {
      tone: 'error',
      text: `${filename} could not be accepted${because}. Nothing was saved. Check the file and send it again, or ask your adviser what to do.`,
    };
  }
  if (handle.state === 'held') {
    return {
      tone: 'warn',
      text: `${filename} needs a closer look before it can be added${because}. Your adviser will be in touch - you do not need to send it again.`,
    };
  }
  if (handle.stillChecking) {
    // The budget ran out with the scan still running, which a large file on a slow scanner will do.
    // Nothing has gone wrong and there is nothing to redo, so this says so plainly.
    return {
      tone: 'warn',
      text: `${filename} was sent and is still being checked. It is safe to close this page - it will be here when you come back.`,
    };
  }
  return null;
}

export function App(): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [data, setData] = useState<ExchangeData | null>(null);
  const [uploading, setUploading] = useState<UploadState | null>(null);
  const [outcome, setOutcome] = useState<UploadOutcome | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isGenericLink, setIsGenericLink] = useState(() => genericTokenFromPath(globalThis.location.pathname) !== null);

  /** Load everything one exchange renders. Three calls, because they are three resources. */
  const loadExchange = useCallback(async (exchangeId: string): Promise<void> => {
    const base = `/exchanges/${encodeURIComponent(exchangeId)}`;
    const [exchange, checklist, documents] = await Promise.all([
      request({ path: base, schema: guestExchangeSchema }),
      request({ path: `${base}/requests`, schema: guestChecklistSchema }),
      request({ path: `${base}/documents`, schema: guestDocumentListSchema }),
    ]);
    setData({ exchange, requests: checklist.requests, documents: documents.documents });
    setPhase({ kind: 'exchange', exchangeId });
  }, []);

  /**
   * The first read after a session exists. D-25.
   *
   * One exchange goes straight in. That is not a shortcut for the demo's benefit: a list of one
   * item is a click that tells the reader nothing, and the overwhelming majority of contacts have
   * exactly one representative. The list earns its place the moment there are two.
   *
   * Throws on failure like `loadExchange` does, because both callers - the arrival check and the
   * verify - already distinguish "no session" from "something broke" by catching.
   */
  const loadEverything = useCallback(async (): Promise<void> => {
    const list = await request({ path: '/exchanges', schema: guestExchangeListSchema });
    const only = list.exchanges.length === 1 ? list.exchanges[0] : undefined;
    if (only !== undefined) {
      await loadExchange(only.exchangeId);
      return;
    }
    setPhase({ kind: 'exchanges', list });
  }, [loadExchange]);

  /**
   * Decide what to show on arrival.
   *
   * **The link decides, and only then the cookie.** The other order is the obvious one and it is wrong:
   * asking for the exchange first and returning as soon as it succeeded meant a token in the path was
   * examined only when no session existed, so a browser holding one person's session opened another
   * person's emailed link straight into the first person's documents, with no code asked for. Found in
   * testing on 2026-08-05. On one person's own two addresses that is confusing; on a family computer or
   * a shared machine it is one person reading another's documents.
   *
   * The session shortcut is kept, because a returning visitor should not be asked for anything - but the
   * server is what says the session belongs to this link's contact, since the surface cannot tell. See
   * `sessionIsAnotherContact`.
   *
   * With no token there is nothing to compare against and a live session is simply the visitor: that is
   * somebody who bookmarked the page or came back to an open tab.
   */
  useEffect(() => {
    const token = tokenFromPath(globalThis.location.pathname);
    const genericToken = genericTokenFromPath(globalThis.location.pathname);

    void (async () => {
      if (genericToken !== null) {
        setIsGenericLink(true);
        try {
          const info = await request({
            method: 'GET',
            path: `/upload-links/${encodeURIComponent(genericToken)}/identify`,
            schema: genericLinkInfoSchema,
          });
          setPhase({ kind: 'generic_identify', repName: info.repName ?? undefined });
          return;
        } catch (err) {
          setPhase({
            kind: 'dead_link',
            detail: err instanceof Error ? err.message : 'This link is no longer available or not valid',
          });
          return;
        }
      }
      if (token === null) {
        try {
          await loadEverything();
        } catch {
          // No session and no link. Since D-25 that is the sign-in page rather than an
          // apology: an address and a code is all it takes, and nothing has to be kept.
          setPhase({ kind: 'sign_in' });
        }
        return;
      }

      let grant: OpenedGrant;
      try {
        grant = await request({ path: `/grants/${encodeURIComponent(token)}`, schema: openedGrantSchema });
      } catch (err) {
        /*
         * The link is spent or expired, and we cannot tell whose it was - a dead grant deliberately
         * discloses nothing. So there is no falling back to whatever session this browser holds: it may
         * be somebody else's, and that is the failure this whole ordering exists to prevent.
         *
         * The cost is that re-opening your own used link while still signed in now says the link is
         * spent rather than showing your documents. That is true, and the screen says where to go
         * instead. DG.15 removes the case entirely by keeping a link usable until it expires.
         */
        setPhase({
          kind: 'dead_link',
          detail: err instanceof Error ? err.message : 'This link is no longer available.',
        });
        return;
      }

      if (!grant.sessionIsAnotherContact) {
        try {
          await loadEverything();
          return;
        } catch {
          // No session, or it lapsed. The grant is live, so the ordinary flow starts here.
        }
      }

      setPhase({ kind: 'grant', grant });
    })();
  }, [loadEverything]);

  const requestCode = useCallback(async (): Promise<ChallengeIssued> => {
    const token = tokenFromPath(globalThis.location.pathname);
    if (token === null) throw new Error('This link is no longer available.');

    return request({
      method: 'POST',
      path: `/grants/${encodeURIComponent(token)}/challenge`,
      schema: challengeIssuedSchema,
    });
  }, []);

  /** Exchange a verified challenge, or a trusted device, for a session. */
  const verify = useCallback(
    async (input: { code?: string; rememberDevice: boolean }): Promise<void> => {
      const token = tokenFromPath(globalThis.location.pathname);
      if (token === null) throw new Error('This link is no longer available.');

      await request({
        method: 'POST',
        path: `/grants/${encodeURIComponent(token)}/verify`,
        body: input,
        schema: verifiedSessionSchema,
      });

      // The grant is spent and the session is a cookie. Scrubbing the path here means a reload does
      // not re-open a consumed grant, and "the page I'm on" is safe to share.
      globalThis.history.replaceState(null, '', '/');
            // Load the post-sign-in list. A generic-link-scoped sign-in is filtered to that rep's
            // exchanges; an ordinary sign-in shows every exchange for the contact.
      await loadEverything();
    },
    [loadEverything],
  );

  const uploadOne = useCallback(
    async (input: { exchangeId: string; file: File; requestId: string | null }): Promise<void> => {
      setError(null);
      setOutcome(null);
      setUploading({
        requestId: input.requestId,
        filename: input.file.name,
        sentBytes: 0,
        totalBytes: input.file.size,
        stage: 'sending',
      });

      try {
        const handle = await uploadFile(input.exchangeId, {
          file: input.file,
          ...(input.requestId === null ? {} : { requestId: input.requestId }),
          onProgress: ({ sentBytes, totalBytes }) => {
            setUploading((current) =>
              current === null ? null : { ...current, sentBytes, totalBytes },
            );
          },
          // The bytes are all sent and the scan has not run. Section 09 makes that wait unavoidable,
          // so the surface names it rather than showing a finished progress bar and nothing else.
          onChecking: () => {
            setUploading((current) => (current === null ? null : { ...current, stage: 'checking' }));
          },
        });

        // Re-read rather than patch local state, and only now: `uploadFile` has waited for the
        // pipeline, so the checklist item and the document list have had a chance to change. Read
        // any earlier and the answer is the state from before this upload existed.
        await loadExchange(input.exchangeId);
        setOutcome(outcomeOf(handle, input.file.name));
      } catch (err) {
        setError(err);
      } finally {
        setUploading(null);
      }
    },
    [loadExchange],
  );

  const upload = useCallback(
    async (input: { exchangeId: string; files: readonly File[]; requestId: string | null }): Promise<void> => {
      for (const file of input.files) {
        await uploadOne({ exchangeId: input.exchangeId, file, requestId: input.requestId });
      }
    },
    [uploadOne],
  );

  switch (phase.kind) {
    case 'loading':
      return (
        <Page title="Secure documents">
          {/* An <output> carries role=status implicitly, so a screen reader announces this without the
              attribute having to be right. `ddds-empty` restores the block layout and quiet colour the
              paragraph had, since an output element is inline by default. */}
          <output className="ddds-empty">Loading…</output>
        </Page>
      );

    case 'sign_in':
      return (
        <SignInScreen
          onRequestCode={async (email) => {
            await request({ method: 'POST', path: '/sign-in', body: { email } });
            // Moved on regardless, because the server does not say whether it knew the
            // address and this screen must not imply it did. `SignInScreen` renders the
            // "check your email" state itself; this phase is what the code goes into.
            setPhase({ kind: 'sign_in_code', email });
          }}
        />
      );

    case 'sign_in_code':
      return (
        <CodeScreen
          // The request deliberately answered nothing, so the surface knows only what the person
          // typed. Both unknowns are null rather than invented: this used to pass `expiresAt: ''`
          // and `attemptsRemaining: 5`, which the types accepted and which put a deadline and an
          // attempt budget on screen that no endpoint had ever stated (DG.11).
          maskedEmail={phase.email}
          expiresAt={null}
          attemptsRemaining={null}
          repName=""
          // The request said nothing about whether the address is known, so neither may
          // this screen. See `CodeScreenProps.addressConfirmed`.
          addressConfirmed={false}
          onVerify={async ({ code }) => {
            await request({
              method: 'POST',
              path: '/sign-in/verify',
              body: {
                email: phase.email,
                code,
                rememberDevice: false,
                ...(phase.genericLinkToken === undefined ? {} : { genericLinkToken: phase.genericLinkToken }),
              },
              schema: signedInSchema,
            });
            await loadEverything();
          }}
          onResend={async () => {
            await request({
              method: 'POST',
              path: '/sign-in',
              body: {
                email: phase.email,
                ...(phase.genericLinkToken === undefined ? {} : { genericLinkToken: phase.genericLinkToken }),
              },
            });
          }}
        />
      );

    case 'generic_identify': {
      const genericToken = genericTokenFromPath(globalThis.location.pathname);
      return (
        <IdentifyScreen
          repName={phase.repName}
          onSubmit={async ({ displayName, email }) => {
            if (genericToken === null) throw new Error('This link is no longer available.');
            await request({
              method: 'POST',
              path: `/upload-links/${encodeURIComponent(genericToken)}/identify`,
              body: { displayName, email },
            });
            setPhase({ kind: 'generic_code', email, genericLinkToken: genericToken, repName: phase.repName });
          }}
        />
      );
    }

    case 'generic_code':
      return (
        <CodeScreen
          maskedEmail={phase.email}
          expiresAt={null}
          attemptsRemaining={null}
          repName={phase.repName ?? ''}
          addressConfirmed={false}
          onVerify={async ({ code }) => {
            await request({
              method: 'POST',
              path: '/sign-in/verify',
              body: { email: phase.email, code, rememberDevice: false, genericLinkToken: phase.genericLinkToken },
              schema: signedInSchema,
            });
            // Verified: the session cookie is set, and only now may the upload screen show. The
            // pipeline derives the sender's name and address from this session, not from anything
            // typed on the previous screen.
            setPhase({ kind: 'generic_upload', genericLinkToken: phase.genericLinkToken, repName: phase.repName });
          }}
          onResend={async () => {
            await request({
              method: 'POST',
              path: '/sign-in',
              body: { email: phase.email, genericLinkToken: phase.genericLinkToken },
            });
          }}
        />
      );

    case 'generic_upload':
      return (
        <GenericUploadScreen
          repName={phase.repName}
          onUpload={async ({ file, onProgress }) => {
            await uploadGenericFile(phase.genericLinkToken, {
              file,
              onProgress: (prog) => {
                onProgress?.(prog.sentBytes, prog.totalBytes);
              },
            });
          }}
        />
      );

    case 'dead_link':
      return (
        <Page eyebrow="Secure documents" title="This link has expired">
          <Notice tone="error">{phase.detail}</Notice>
          <p className="ddds-footer">
            Links are single use and last seven days. Ask your adviser to send a new one.
          </p>
        </Page>
      );

    case 'grant':
      return (
        <GrantScreen
          grant={phase.grant}
          onRequestCode={async () => {
            const challenge = await requestCode();
            setPhase({ kind: 'code', grant: phase.grant, challenge });
          }}
          onUseDevice={() => verify({ rememberDevice: false })}
        />
      );

    case 'code':
      return (
        <CodeScreen
          // The link path is the one that knows: a live grant carries the masked address, and the
          // challenge response carries the real deadline that was fetched and parsed all along.
          maskedEmail={phase.challenge.maskedEmail}
          expiresAt={phase.challenge.expiresAt}
          attemptsRemaining={phase.challenge.attemptsRemaining}
          repName={phase.grant.repName}
          onVerify={async ({ code }) => verify({ code, rememberDevice: false })}
          onResend={async () => {
            const challenge = await requestCode();
            setPhase({ kind: 'code', grant: phase.grant, challenge });
          }}
        />
      );

    case 'exchanges':
      return (
        <ExchangeListScreen
          contactName={phase.list.contactName}
          exchanges={phase.list.exchanges}
          error={error}
          onOpen={(exchangeId) => {
            setError(null);
            setOutcome(null);
            void loadExchange(exchangeId).catch(setError);
          }}
        />
      );

    case 'exchange': {
      const exchangeId = phase.exchangeId;
      return data === null ? (
        <Page title="Secure documents">
          <FailureNotice error={error} />
        </Page>
      ) : (
        <ExchangeScreen
          exchange={data.exchange}
          requests={data.requests}
          documents={data.documents}
          uploading={uploading}
          outcome={outcome}
          onUpload={(input) => upload({ ...input, exchangeId })}
          error={error}
          isGenericLink={isGenericLink}
        />
      );
    }
  }
}
